/**
 * Picker scope (ORCH-332, ORCH-21 §10.11)
 *
 * Purpose: Tells list pickers which permission the surrounding form exercises, so they
 *          offer only the master-data values it reaches: a create-plan form offers
 *          what mediaplan.create allows, an MDM value form what mdm.edit allows.
 * Logic: A form (or modal) wraps its fields in <PickerScope permission={...}>. With no
 *        provider, or permission null, pickers offer every value visible in the tenant,
 *        as before. Every modal that renders list fields sets its own scope, because
 *        context passes through MUI portals: a dialog opened from a plan form would
 *        otherwise inherit the plan's permission. Picker filtering is UX; the server
 *        checks every save.
 *        <PickerTenant tenantId={...}> names the tenant whose values pickers offer when
 *        it isn't the signed-in one: a system admin in GLOBAL managing a membership in
 *        another tenant (ORCH-336). Without it pickers use the active tenant. Inline
 *        create is off while it names another tenant, so picking never writes there.
 *        <PickerValues values={{ fd_x: [...ids] }}> limits a list field to the given
 *        values (GLOBAL ids): the Organization of a membership offers only the
 *        organizations its tenant is enabled for (ORCH-398). A field not named is
 *        untouched, and a limited field has no inline create.
 *        <PickerFilters modelId values locked> (ORCH-400) turns the form's current values
 *        into each picker's `filter_by`: a picker whose list has a related field that
 *        another field on the form (or an ancestor record) holds offers only matching
 *        values. The graph comes from the server (usePickerFilterGraph), so nothing is
 *        written per form. It also carries the ids locked for this user: stored values
 *        outside their access, shown without a delete icon. A form provides its own, so
 *        the innermost wins through a modal's portal.
 *        <PickerAncestors records={{ mediaplan: planValues }}> names the ancestor records
 *        a child form is opened under, keyed by model_id, for subset and ancestor
 *        related filtering. No page provides it yet (no flight or line-item form).
 */

import React, { createContext, useContext, useMemo } from 'react';
import { PermissionKey } from '../constants/permissionKeys';
import { usePickerFilterGraph } from '../hooks/usePickerFilterGraph';
import { asList } from '../utils/listValues';

const PickerScopeContext = createContext<PermissionKey | null>(null);

export const PickerScope: React.FC<{
  permission: PermissionKey | null;
  children: React.ReactNode;
}> = ({ permission, children }) => (
  <PickerScopeContext.Provider value={permission}>{children}</PickerScopeContext.Provider>
);

/** The permission pickers here are scoped to, or null for every visible value. */
export const usePickerScope = (): PermissionKey | null => useContext(PickerScopeContext);

const PickerTenantContext = createContext<string | null>(null);

export const PickerTenant: React.FC<{
  tenantId: string | null;
  children: React.ReactNode;
}> = ({ tenantId, children }) => (
  <PickerTenantContext.Provider value={tenantId}>{children}</PickerTenantContext.Provider>
);

/** The tenant pickers here read values from, or null for the active tenant. */
export const usePickerTenant = (): string | null => useContext(PickerTenantContext);

const PickerValuesContext = createContext<Record<string, string[]> | null>(null);

export const PickerValues: React.FC<{
  values: Record<string, string[]> | null;
  children: React.ReactNode;
}> = ({ values, children }) => (
  <PickerValuesContext.Provider value={values}>{children}</PickerValuesContext.Provider>
);

/** The only values a list field may offer here (GLOBAL ids), or null when it isn't limited. */
export const usePickerValues = (fielddefId: string): string[] | null =>
  useContext(PickerValuesContext)?.[fielddefId] ?? null;

// ---------------------------------------------------------------------------
// ORCH-400: ancestors and related-field filters
// ---------------------------------------------------------------------------

export type PickerAncestorRecords = Record<string, Record<string, unknown>>;

const NO_ANCESTORS: PickerAncestorRecords = {};
const PickerAncestorsContext = createContext<PickerAncestorRecords>(NO_ANCESTORS);

export const PickerAncestors: React.FC<{
  records: PickerAncestorRecords;
  children: React.ReactNode;
}> = ({ records, children }) => (
  <PickerAncestorsContext.Provider value={records}>{children}</PickerAncestorsContext.Provider>
);

/** The ancestor records this form is opened under, by model_id (empty when none). */
export const usePickerAncestors = (): PickerAncestorRecords => useContext(PickerAncestorsContext);

interface PickerFiltersValue {
  /** fielddef_id -> its filter_by entries joined with '|' (one string, so pickers key on it) */
  entries: Record<string, string>;
  locked: Record<string, string[]>;
}

const PickerFiltersContext = createContext<PickerFiltersValue | null>(null);
const NO_LOCKED_FIELDS: Record<string, string[]> = {};

/**
 * Each picker's `filter_by` from the graph and the values it can see
 *
 * Logic: for every enabled, non-excluded source of a picker, read the source's value
 *        from the form (same model) or from the ancestor record (other model); a source
 *        with a selection becomes "<fielddef_id>:<id,...>". Entries are sorted so equal
 *        selections give equal strings.
 */
export interface PickerFilterGraphLike {
  fields: Array<{
    fielddef_id: string;
    enabled: boolean;
    sources: Array<{ model_id: string; fielddef_id: string; excluded: boolean }>;
  }>;
}

export const buildFilterEntries = (
  graph: PickerFilterGraphLike | undefined,
  modelId: string,
  values: Record<string, unknown>,
  ancestors: PickerAncestorRecords
): Record<string, string> => {
  const out: Record<string, string> = {};
  graph?.fields.forEach((filter) => {
    if (!filter.enabled) return;
    const entries = filter.sources
      .filter((source) => !source.excluded)
      .map((source) => {
        const holder = source.model_id === modelId ? values : ancestors[source.model_id];
        const ids = asList(holder?.[source.fielddef_id]).map(String);
        return ids.length ? `${source.fielddef_id}:${ids.join(',')}` : null;
      })
      .filter((entry): entry is string => entry !== null)
      .sort();
    if (entries.length) out[filter.fielddef_id] = entries.join('|');
  });
  return out;
};

export const PickerFilters: React.FC<{
  modelId: string;
  tenantId: string;
  values: Record<string, unknown>;
  locked?: Record<string, string[]>;
  children: React.ReactNode;
}> = ({ modelId, tenantId, values, locked, children }) => {
  const { graph } = usePickerFilterGraph(modelId, tenantId);
  const ancestors = usePickerAncestors();
  const entries = useMemo(
    () => buildFilterEntries(graph, modelId, values, ancestors),
    [graph, modelId, values, ancestors]
  );
  const value = useMemo(() => ({ entries, locked: locked ?? NO_LOCKED_FIELDS }), [entries, locked]);
  return <PickerFiltersContext.Provider value={value}>{children}</PickerFiltersContext.Provider>;
};

const NO_FILTERS: string[] = [];

/** The filter_by entries a picker sends (stable while its sources' selections stand). */
export const usePickerFilters = (fielddefId: string): string[] => {
  const joined = useContext(PickerFiltersContext)?.entries[fielddefId] ?? '';
  return useMemo(() => (joined ? joined.split('|') : NO_FILTERS), [joined]);
};

const NO_LOCKED: ReadonlySet<string> = new Set();

/** The stored ids of a field this user can't remove (empty on create forms). */
export const usePickerLockedValues = (fielddefId: string): ReadonlySet<string> => {
  const ids = useContext(PickerFiltersContext)?.locked[fielddefId];
  const joined = ids?.join('|') ?? '';
  return useMemo(() => (joined ? new Set(joined.split('|')) : NO_LOCKED), [joined]);
};
